import OnboardingBody from "../components/OnboardingBody";
import OnboardingSidebar from "../components/OnboardingSidebar";
import TariffsList from "./components/TariffsList";
import CancellationModal from "./components/modals/CancellationModal";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import classNames from "classnames";
import { FC, useCallback, useMemo, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import inactive from "~@/assets/image/svg/onboarding/inactive.svg";
import pricing from "~@/assets/image/svg/onboarding/pricing.svg";
import Loader from "~@/components/base/Loader";
import FullExportButton from "~@/components/dealer-export/FullExportButton";
import TechnicalError from "~@/components/layout/TechnicalError";
import ToastContent from "~@/components/layout/ToastContent";
import {
  DealershipType,
  MethodType,
  OnboardStatus,
  OrganizationStatus,
  useGetOrganizationsQuery,
  useMakeOrganizationMutation,
} from "~@/graphql/codegen/generated";
import { ArrowLeft14x14Icon, Check14x14Icon } from "~@/icons";
import { RootState, useAppSelector } from "~@/store";

const Pricing: FC = () => {
  const [isUnsubscribeModalOpen, setIsUnsubscribeModalOpen] = useState(false);

  const openUnsubscribeModal = useCallback(() => {
    setIsUnsubscribeModalOpen(true);
  }, []);

  const closeUnsubscribeModal = useCallback(() => {
    setIsUnsubscribeModalOpen(false);
  }, []);

  const { t } = useTranslation();

  const selectedTariff = useAppSelector(
    (state: RootState) => state.pricing.selectedTariff
  );

  const { data: organizationsData } = useGetOrganizationsQuery();

  const currentOrganization = useMemo(() => {
    return organizationsData?.getOrganizations?.current;
  }, [organizationsData]);

  const navigate = useNavigate();

  const isInactive = useMemo(() => {
    return (
      (currentOrganization?.status === OrganizationStatus.Inactive &&
        (currentOrganization.isCanceled ||
          currentOrganization.isPaymentFailed)) ??
      false
    );
  }, [currentOrganization]);

  const [makeOrganization, { loading: isOrganizationUpdating }] =
    useMakeOrganizationMutation({
      onCompleted() {
        if (isInactive) {
          navigate("/payment-method");
        } else {
          navigate("/welcome/signing");
        }
      },
      onError() {
        toast(
          <ToastContent
            type="error"
            message={t("graphqlError.update-organization")}
          />
        );
      },
    });

  const isTrial = useMemo(() => {
    return currentOrganization?.isTrial;
  }, [currentOrganization]);

  const chooseTariff = useCallback(() => {
    if (!currentOrganization || !selectedTariff) return;

    return makeOrganization({
      variables: {
        input: {
          method: MethodType.Update,
          id: currentOrganization.id,
          tariffId: selectedTariff.id,
        },
      },
    });
  }, [currentOrganization, selectedTariff, makeOrganization]);

  const resetOrganization = useCallback(() => {
    if (!currentOrganization) return;

    return makeOrganization({
      variables: {
        input: {
          id: currentOrganization.id,
          method: MethodType.Update,
          onboardStatus: OnboardStatus.Done,
        },
      },
      optimisticResponse: {
        makeOrganization: {
          ...currentOrganization,
          onboardStatus: OnboardStatus.Done,
        },
      },
    });
  }, [currentOrganization, makeOrganization]);

  return (
    <>
      <div className="onboarding-container">
        <OnboardingSidebar showSteps={!isInactive}>
          <div className="block block--has-mb-25">
            <figure className="image image--is-auto">
              <img
                src={isInactive ? inactive : pricing}
                alt="cloud icon"
                width={160}
              />
            </figure>
          </div>
          <div className="block block--has-mb-17">
            <h1 className="onboarding__title">
              {isInactive
                ? t("onboarding.title.inactive")
                : currentOrganization?.dealershipType === DealershipType.New
                ? t("onboarding.title.pricing")
                : t("onboarding.title.choose-pricing")}
            </h1>
          </div>
          <div className="block">
            <span
              className="onboarding__note"
              style={{
                textAlign: isInactive ? "center" : "start",
              }}
            >
              {isInactive
                ? t("onboarding.pricing.cancel-description")
                : t("onboarding.pricing.description")}
            </span>
            {isInactive ? <FullExportButton /> : null}
          </div>
        </OnboardingSidebar>
        <OnboardingBody>
          <div className="hero hero--is-scrollable hero--is-fullheight">
            <div className="hero__header">
              {!isInactive && isTrial && (
                <a
                  href="#back"
                  className="onboarding__back-button"
                  onClick={(e) => {
                    e.preventDefault();
                    resetOrganization();
                  }}
                >
                  <div className="icon-text icon-text--has-gap-6">
                    <span className="icon icon--is-24x24">
                      <ArrowLeft14x14Icon />
                    </span>
                    <span>{t("action.go-back")}</span>
                  </div>
                </a>
              )}
            </div>
            <div className="hero__body">
              <div className="container">
                <div className="block block--has-mb-28">
                  <TariffsList />
                </div>
                <div className="block block--is-self-centered">
                  <div className="buttons">
                    <button
                      className={classNames(
                        "button button--h-48 button--fill-black-0",
                        {
                          "button--is-loading": isOrganizationUpdating,
                          "button--is-disabled": !selectedTariff,
                        }
                      )}
                      type="button"
                      disabled={!selectedTariff}
                      onClick={chooseTariff}
                    >
                      <span>
                        {isInactive
                          ? t("action.restore-account")
                          : t("action.go-to-next-step")}
                      </span>
                      <span className="icon">
                        <Check14x14Icon />
                      </span>
                    </button>
                  </div>
                </div>
                {currentOrganization?.isTrial && (
                  <div className="block block--is-self-centered">
                    <a
                      className="onboarding__back-button onboarding__back-button--is-red-0"
                      href="#cancel-account"
                      onClick={(e) => {
                        e.preventDefault();

                        openUnsubscribeModal();
                      }}
                    >
                      {t("action.cancel-account")}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </OnboardingBody>
      </div>
      <CancellationModal
        isModalOpen={isUnsubscribeModalOpen}
        closeModal={closeUnsubscribeModal}
      />
    </>
  );
};

export default withAuthenticationRequired(
  withErrorBoundary(Pricing, {
    FallbackComponent: () => <TechnicalError />,
  }),
  {
    onRedirecting() {
      return <Loader isFullHeight />;
    },
  }
);
